// The four documents theatre actually puts on paper.
//
// Until now every "Print" button in OT called window.print(), which prints the
// browser's view of the page — sidebar, navigation, buttons and all — on
// whatever paper happens to be loaded. A call slip that comes out with a
// navigation menu down one side is not a document a porter can act on, and a
// consent form is not something you print out of a viewport at all.
//
//   Call slip       — the porter's instruction: who to fetch, from where, to
//                     which theatre, for what, and who asked.
//   Final list      — the day's authorised list, one line per case, with the
//                     allergy and bio-hazard lines that are the whole reason a
//                     paper list still exists.
//   Operative record— the merged note: header, the three notes, counts and
//                     what was used. The signed account of the operation.
//   Consent form    — the blank form, pre-filled with the patient and the
//                     procedure, for the patient to sign. There is no way to
//                     print a signed consent the system does not hold; what a
//                     theatre needs is the form with the right details on it.
//
// The layout primitives come from the inventory module's pdfTheme, deliberately.
// It is the house PDF style — letterhead, cards, tables, footers — and a second
// one invented here would make an OT document look like it came from a
// different hospital than the purchase order.

import jsPDF from "jspdf";
import "jspdf-autotable";
import dayjs from "dayjs";

import {
  C,
  SIDE_M,
  U,
  CARD_W,
  CARD_X_RIGHT,
  CARD_GAP,
  CARD_PAD_X,
  CONT_TOP,
  DASH,
  displayOrDash,
  drawCardFrame,
  drawContinuationHeader,
  drawLetterhead,
  drawLines,
  drawRowRule,
  drawRows,
  drawSignatureRow,
  drawTitleRow,
  ensureSpace,
  measureLines,
  measureRows,
  stampFooters,
  tableStyles,
} from "../../inventory/utils/pdfTheme";
import {
  formatRegistrationLine,
  resolveFacilityIdentity,
} from "../../inventory/utils/facilityIdentity";
import { resolveLogo } from "../../../utils/reportLetterhead";
import { formatIstDate, formatIstDateTime, formatIstTime } from "../../../utils/istDateTime";

const NAVY = C.navy;

/** Everything every OT document opens with. Returns the doc and the y to build from. */
function startDoc({ title, subtitle, chipLabel, chipColor = NAVY }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const identity = resolveFacilityIdentity();
  const y = drawLetterhead(doc, identity, formatRegistrationLine(identity), resolveLogo(), true);
  return {
    doc,
    identity,
    y: drawTitleRow(doc, y, { title, subtitle, chipLabel, chipColor }),
  };
}

/** A label/value card, measured and drawn in one call. @returns {number} its bottom y */
function card(doc, x, y, w, title, rows) {
  const { counts, height } = measureRows(doc, rows, w);
  const bodyTop = drawCardFrame(doc, x, y, w, height, title);
  drawRows(doc, x, bodyTop, w, rows, counts);
  return y + height;
}

/** A prose card. @returns {number} its bottom y */
function textCard(doc, x, y, w, title, text) {
  const { lines, height, pitch } = measureLines(doc, text || DASH, w);
  const bodyTop = drawCardFrame(doc, x, y, w, height, title);
  drawLines(doc, x, bodyTop, lines, pitch);
  return y + height;
}

/** Two cards side by side. @returns {number} the bottom of the taller one */
function cardPair(doc, y, left, right) {
  const leftRows = measureRows(doc, left.rows, CARD_W);
  const rightRows = measureRows(doc, right.rows, CARD_W);
  const height = Math.max(leftRows.height, rightRows.height);

  let top = drawCardFrame(doc, SIDE_M, y, CARD_W, height, left.title);
  drawRows(doc, SIDE_M, top, CARD_W, left.rows, leftRows.counts);
  top = drawCardFrame(doc, CARD_X_RIGHT, y, CARD_W, height, right.title);
  drawRows(doc, CARD_X_RIGHT, top, CARD_W, right.rows, rightRows.counts);

  return y + height;
}

function open(doc) {
  const url = URL.createObjectURL(doc.output("blob"));
  window.open(`${url}#zoom=page-fit`, "_blank", "noopener");
}

// ── 1. Call slip ──────────────────────────────────────────────────────────────

/**
 * The porter's instruction, on one sheet.
 *
 * A WARNING BAND, not a card, carries the allergy and the bio-hazard: those two
 * facts are why the slip travels with the patient, and a porter reading it in a
 * corridor must not have to find them among ten other rows.
 */
export function printCallSlip(slip, otCase = {}) {
  const { doc, identity, y } = startDoc({
    title: "Theatre Call Slip",
    subtitle: `Issued ${formatIstDateTime(slip.issued_at) || DASH} by ${
      slip.issued_by_name || DASH
    }`,
    chipLabel: (slip.porter_task_status || "ISSUED").replace(/_/g, " "),
  });

  let next = cardPair(doc, y, {
    title: "Patient",
    rows: [
      ["Name", slip.patient_name],
      ["MRN", slip.health_record_id],
      ["Age / sex", [slip.age ?? otCase.age, slip.gender || otCase.gender].filter(Boolean).join(" / ")],
      ["Ward / bed", `${slip.ward_name || DASH} / ${slip.bed_label || DASH}`],
    ],
  }, {
    title: "Theatre",
    rows: [
      ["Theatre", slip.operating_room_name],
      ["Scheduled", formatIstDateTime(slip.scheduled_start)],
      ["Procedure", slip.procedure_name],
      ["Diagnosis", slip.diagnosis || otCase.diagnosis],
      ["Surgeon", otCase.primary_surgeon_name],
    ],
  });

  // The two facts that must not be missed.
  const warnings = [
    otCase.known_allergies && `ALLERGY: ${otCase.known_allergies}`,
    otCase.biohazard &&
      `BIO-HAZARD${otCase.biohazard_note ? `: ${otCase.biohazard_note}` : ""}`,
  ].filter(Boolean);

  if (warnings.length) {
    next += CARD_GAP;
    const boxH = 6 + warnings.length * 5;
    doc.setDrawColor(...C.navy);
    doc.setLineWidth(0.6);
    doc.rect(SIDE_M, next, U, boxH, "S");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.setTextColor(...C.navy);
    warnings.forEach((w, i) => {
      doc.text(w, SIDE_M + CARD_PAD_X, next + 6.5 + i * 5);
    });
    next += boxH;
  }

  const slipNotes = [slip.porter_note, slip.special_instructions]
    .filter(Boolean)
    .join(" · ");
  if (slipNotes) {
    next = textCard(doc, SIDE_M, next + CARD_GAP, U, "Notes for the porter", slipNotes);
  }

  // Signed on collection and on arrival — the slip is also the handover record.
  drawSignatureRow(doc, next + 6, "Ward nurse — patient handed over", "Theatre — patient received");

  stampFooters(doc, identity, true);
  open(doc);
}

// ── 2. Final list ─────────────────────────────────────────────────────────────

/**
 * The day's operating list.
 *
 * One row per case, ordered as the day runs. The allergy and bio-hazard go in a
 * second line under the patient rather than a column of their own, because they
 * are absent on most rows and a mostly-empty column reads as "nothing to see".
 */
export function printFinalList(dateIso, data) {
  const cases = data?.cases || [];
  const day = dayjs(dateIso).format("dddd, D MMMM YYYY");

  const { doc, identity, y } = startDoc({
    title: "Final OT List",
    subtitle: day,
    chipLabel: data?.authorised_only ? "AUTHORISED ONLY" : "ALL BOOKED",
  });

  doc.autoTable({
    ...tableStyles(),
    startY: y + 2,
    margin: { left: SIDE_M, right: SIDE_M, top: CONT_TOP },
    head: [["Time", "Theatre", "Patient", "Procedure", "Surgeon / anaesthetist", "Status"]],
    columnStyles: {
      0: { cellWidth: 20 },
      1: { cellWidth: 24 },
      2: { cellWidth: 42 },
      3: { cellWidth: 44 },
      4: { cellWidth: 38 },
      5: { cellWidth: 22 },
    },
    body: cases.map((c) => [
      `${formatIstTime(c.start_datetime)}\n${formatIstTime(c.end_datetime)}`,
      displayOrDash(c.operating_room_name),
      [
        `${c.patient_name || DASH}`,
        [c.health_record_id, c.age != null ? `${c.age}${c.gender ? `/${c.gender}` : ""}` : null]
          .filter(Boolean)
          .join(" · "),
        c.ward_name || "",
        c.known_allergies ? `ALLERGY: ${c.known_allergies}` : "",
        c.biohazard ? `BIO-HAZARD${c.biohazard_note ? `: ${c.biohazard_note}` : ""}` : "",
      ]
        .filter(Boolean)
        .join("\n"),
      [
        c.procedure_name || DASH,
        c.diagnosis ? `Dx: ${c.diagnosis}` : "",
        c.special_instructions || "",
      ]
        .filter(Boolean)
        .join("\n"),
      [c.primary_surgeon_name || DASH, c.anaesthetist_name || DASH].join("\n"),
      [
        c.lifecycle_status || DASH,
        c.scheduling_type || "",
        c.reschedule_count > 0 ? `MOVED ${c.reschedule_count}x` : "",
      ]
        .filter(Boolean)
        .join("\n"),
    ]),
    didDrawCell: (d) => {
      if (d.column.index === 0) {
        drawRowRule(doc, d, SIDE_M, SIDE_M + U);
      }
    },
    didDrawPage: (d) => {
      if (d.pageNumber > 1) {
        drawContinuationHeader(doc, identity, "Final OT List", day, null, true);
      }
    },
  });

  const endY = doc.lastAutoTable?.finalY || y;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  doc.setTextColor(...C.muted);
  doc.text(
    `${cases.length} case(s).  Printed ${formatIstDateTime(new Date().toISOString())}.`,
    SIDE_M,
    endY + 6,
  );

  drawSignatureRow(doc, endY + 8, "OT manager", "Anaesthetist in charge");

  stampFooters(doc, identity, true);
  open(doc);
}

// ── 3. Operative record ───────────────────────────────────────────────────────

/** The note fields worth printing, per kind, in reading order. */
const RECORD_SECTIONS = {
  surgical: [
    ["pre_op_diagnosis", "Pre-operative diagnosis"],
    ["post_op_diagnosis", "Post-operative diagnosis"],
    ["procedure_performed", "Procedure performed"],
    ["operative_findings", "Findings"],
    ["procedure_details", "Details"],
    ["specimens", "Specimens"],
    ["implants_used", "Implants"],
    ["estimated_blood_loss", "Estimated blood loss"],
    ["complications", "Complications"],
    ["closure_details", "Closure"],
    ["drains", "Drains"],
    ["wound_class", "Wound class"],
    ["post_op_instructions", "Post-operative instructions"],
    ["vte_prophylaxis", "VTE prophylaxis"],
  ],
  anaesthesia: [
    ["asa_class", "ASA class"],
    ["anaesthesia_technique", "Technique"],
    ["airway_management", "Airway"],
    ["estimated_blood_loss", "Estimated blood loss"],
    ["fluids_given", "Fluids"],
    ["blood_products", "Blood products"],
    ["complications", "Complications"],
    ["lowest_temperature", "Lowest temperature"],
    ["recovery_instructions", "Recovery instructions"],
  ],
  nursing: [
    ["scrub_nurse_name", "Scrub nurse"],
    ["circulating_nurse_name", "Circulating nurse"],
    ["positioning", "Positioning"],
    ["skin_preparation", "Skin preparation"],
    ["diathermy_site", "Diathermy site"],
    ["tourniquet_details", "Tourniquet"],
    ["implants_used", "Implants"],
    ["specimens_sent", "Specimens sent"],
    ["disposals", "Disposals"],
    ["skin_condition", "Skin condition"],
    ["notes", "Notes"],
  ],
};

/** Rows for a note, dropping every field the writer left blank. */
function noteRows(note, spec) {
  if (!note) return [];
  return spec
    .map(([key, label]) => [label, note[key]])
    .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== "");
}

/**
 * The operation as one signed document (OT_0078/0079).
 *
 * An uncertified note still prints — a case can be discussed before it is
 * signed off — but it is stamped DRAFT at the top of its section, because a
 * printed page loses every clue the screen gives about what is finished.
 */
export function printOperativeRecord(merged) {
  const h = merged?.header || {};
  const anyCertified = ["surgical", "anaesthesia", "nursing"].some(
    (k) => merged?.[k]?.certified_at,
  );

  const { doc, identity, y } = startDoc({
    title: "Operative Record",
    subtitle: `${h.primary_procedure || DASH}  ·  ${formatIstDate(h.date) || DASH}`,
    chipLabel: anyCertified ? "CERTIFIED" : "DRAFT",
  });

  let next = cardPair(doc, y, {
    title: "Patient",
    rows: [
      ["Name", h.patient_name],
      ["MRN", h.health_record_id],
      ["Age / sex", [h.age, h.gender].filter(Boolean).join(" / ")],
      ["Ward", h.ward_name],
      ["Case no.", h.case_number],
    ],
  }, {
    title: "Operation",
    rows: [
      ["Theatre", h.place],
      ["Type", h.case_type],
      ["Into theatre", formatIstDateTime(h.in_or_at)],
      ["Out of theatre", formatIstDateTime(h.out_of_or_at)],
      ["Surgeon", h.primary_surgeon_name],
      ["Anaesthetist", h.anaesthetist_name],
    ],
  });

  const team = merged?.team || [];
  if (team.length) {
    next = card(
      doc, SIDE_M, next + CARD_GAP, U, "Team",
      team.map((m) => [
        m.attendee_type_name || "Member",
        [m.member_name, m.grade].filter(Boolean).join(" · "),
      ]),
    );
  }

  ["surgical", "anaesthesia", "nursing"].forEach((kind) => {
    const note = merged?.[kind];
    const rows = noteRows(note, RECORD_SECTIONS[kind]);
    if (!rows.length) return;

    const title = `${kind} note${note?.certified_at ? "" : " — DRAFT"}`;
    const { height } = measureRows(doc, rows, U);
    next = ensureSpace(doc, next + CARD_GAP, height + 6, (d) =>
      drawContinuationHeader(d, identity, "Operative Record", h.case_number, null, true),
    );
    next = card(doc, SIDE_M, next, U, title, rows);

    if (note?.certified_at) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(6.5);
      doc.setTextColor(...C.faint);
      doc.text(
        `Certified ${formatIstDateTime(note.certified_at)} by ${note.certified_by_name || DASH}`,
        SIDE_M + CARD_PAD_X,
        next + 3.4,
      );
      next += 4;
    }
  });

  // The counts are the legal record that nothing was left inside the patient,
  // so a discrepancy prints as its own line rather than a column value.
  const counts = merged?.counts || [];
  if (counts.length) {
    const rows = counts.map((c) => [
      `${c.item_type || "Item"}${c.item_name ? ` — ${c.item_name}` : ""}`,
      [
        `initial ${c.initial_count ?? DASH}`,
        c.added_count ? `added ${c.added_count}` : null,
        `at closure ${c.closure_count ?? DASH} of ${c.expected_at_closure ?? DASH}`,
        c.result === "DISCREPANCY"
          ? `DISCREPANCY${c.discrepancy_reason ? ` — ${c.discrepancy_reason}` : ""}`
          : c.result,
      ]
        .filter(Boolean)
        .join(" · "),
    ]);
    const { height } = measureRows(doc, rows, U);
    next = ensureSpace(doc, next + CARD_GAP, height, (d) =>
      drawContinuationHeader(d, identity, "Operative Record", h.case_number, null, true),
    );
    next = card(doc, SIDE_M, next, U, "Counts", rows);
  }

  const consumption = merged?.consumption || [];
  if (consumption.length) {
    const rows = consumption.map((c) => [
      c.item_name || "Item",
      [c.quantity, c.unit].filter(Boolean).join(" "),
    ]);
    const { height } = measureRows(doc, rows, U);
    next = ensureSpace(doc, next + CARD_GAP, height, (d) =>
      drawContinuationHeader(d, identity, "Operative Record", h.case_number, null, true),
    );
    next = card(doc, SIDE_M, next, U, "Consumed", rows);
  }

  drawSignatureRow(doc, next + 6, "Surgeon", "Anaesthetist");

  stampFooters(doc, identity, true);
  open(doc);
}

// ── 4. Consent form ───────────────────────────────────────────────────────────

/**
 * The consent form, with the patient and the operation already on it.
 *
 * THE SYSTEM DOES NOT HOLD A SIGNED CONSENT to print — consent is recorded as a
 * decision, with a scan attached where one exists. What a theatre needs printed
 * is the FORM: the right patient, the right operation, the right side, so the
 * one thing that must not be wrong is not handwritten at the bedside.
 *
 * The declaration text is deliberately plain and short. It is a starting point a
 * hospital replaces with its own approved wording, not a legal instrument this
 * module invented.
 */
export function printConsentForm(otCase, procedureName) {
  const { doc, identity, y } = startDoc({
    title: "Informed Surgical Consent Form",
    subtitle: "Statutory Surgical Consent · To be verified prior to patient transfer to theatre",
    chipLabel: (otCase.consent?.status || "PENDING").replace(/_/g, " "),
  });

  let next = cardPair(doc, y, {
    title: "Patient Particulars & Clinical Identifiers",
    rows: [
      ["Name", otCase.patient_name || DASH],
      ["MRN / UHID", otCase.health_record_id || otCase.patient_id || DASH],
      ["Age / sex", [otCase.age, otCase.gender].filter(Boolean).join(" / ") || DASH],
      ["Ward / Bed", `${otCase.ward_name || DASH} / ${otCase.bed_number || DASH}`],
    ],
  }, {
    title: "Planned Surgical Procedure",
    rows: [
      ["Procedure", procedureName || otCase.procedure_name || DASH],
      ["Diagnosis", otCase.diagnosis || DASH],
      ["Surgeon", otCase.primary_surgeon_name || DASH],
      ["Case no.", otCase.case_number || DASH],
    ],
  });

  next = textCard(
    doc, SIDE_M, next + CARD_GAP, U, "Statutory Surgical Informed Consent Declaration",
    "1. CLINICAL COUNSELING & INFORMED EXPLANATION:\n" +
      "I confirm that the nature and purpose of the operation named above, its likely benefits, " +
      "the risks involved and the alternatives to it have been explained to me in a language I clearly understand. " +
      "I have had the opportunity to ask questions and all queries have been answered satisfactorily.\n\n" +
      "2. UNANTICIPATED CLINICAL FINDINGS & EMERGENCY PROCEDURES:\n" +
      "I understand that additional procedures may become necessary during the operation, and I consent to " +
      "those that are required in the surgical judgment of the operating team to save my life or to prevent serious harm.\n\n" +
      "3. ANESTHESIA ADMINISTRATION & HISTOPATHOLOGICAL EXAMINATION:\n" +
      "I consent to the administration of anaesthesia, blood or component transfusion if medically required, " +
      "and to the pathological examination and ethical disposal of any tissue removed.",
  );

  // Side, spelt out, above the signatures — wrong-side surgery is the never-event
  // this form exists to prevent, and it must not be reachable by a tick alone.
  next += CARD_GAP;
  doc.setDrawColor(...C.navy);
  doc.setLineWidth(0.6);
  doc.rect(SIDE_M, next, U, 14, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...C.navy);
  doc.text("SIDE / SITE (write in full — left, right, bilateral, not applicable):", SIDE_M + CARD_PAD_X, next + 5.5);
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.2);
  doc.line(SIDE_M + CARD_PAD_X, next + 11, SIDE_M + U - CARD_PAD_X, next + 11);
  next += 14;

  drawSignatureRow(doc, next + 8, "Patient / Authorized Relative / Next of Kin", "Date & Time");
  drawSignatureRow(doc, next + 26, "Primary Operating Surgeon (Counseling & Explanation)", "Healthcare Witness / Interpreter");

  stampFooters(doc, identity, true);
  open(doc);
}

// ── 5. Anesthesia consent form (NABH COP.13) ──────────────────────────────────

/**
 * Pre-Anesthesia Informed Consent Form (NABH COP.13 Statutory Standard).
 *
 * Pre-fills patient particulars, surgical procedure, assigned anesthesiologist,
 * proposed anesthesia technique (GA / Regional / Sedation / MAC), ASA grade,
 * airway/dental assessment notes, and statutory declaration.
 */
export function printAnesthesiaConsentForm(otCase = {}, pac = {}) {
  const procedureName =
    (otCase.procedures || []).find((p) => p.is_primary)?.procedure_name ||
    otCase.procedure_name ||
    "Surgical Procedure";

  const { doc, identity, y } = startDoc({
    title: "Pre-Anesthesia Informed Consent (NABH COP.13)",
    subtitle: "Statutory Informed Consent · To be signed prior to administration of anesthesia",
    chipLabel: (pac.outcome || otCase.consent?.status || "PAC CLEARED").replace(/_/g, " "),
    chipColor: [15, 118, 110],
  });

  const pacData = pac.assessment_data || {};
  const airway = pacData.airway || {};
  const dentition = airway.dentures
    ? "Dentures Present (Removed)"
    : airway.loose_teeth
    ? "Loose / Vulnerable Teeth Noted"
    : "Intact Dentition";

  const bloodReq = pac.blood_units_required
    ? typeof pac.blood_units_required === "object"
      ? `${pac.blood_units_required.prbc || 0} PRBC, ${pac.blood_units_required.ffp || 0} FFP`
      : `${pac.blood_units_required} Units`
    : "None / Type & Screen";

  let next = cardPair(
    doc,
    y,
    {
      title: "Patient Particulars & Clinical Identifiers",
      rows: [
        ["Name", otCase.patient_name || DASH],
        ["MRN / UHID", otCase.health_record_id || otCase.patient_id || DASH],
        [
          "Age / Sex",
          [otCase.age, otCase.gender].filter(Boolean).join(" / ") || DASH,
        ],
        ["Ward / Bed", `${otCase.ward_name || DASH} / ${otCase.bed_number || DASH}`],
        ["Blood Group", otCase.blood_group || pacData.demographics?.blood_group || DASH],
      ],
    },
    {
      title: "Surgical Case & Perioperative Team",
      rows: [
        ["Procedure", procedureName],
        ["Surgeon", otCase.primary_surgeon_name || DASH],
        [
          "Anaesthetist",
          pac.anaesthetist_name || otCase.anaesthetist_name || "Consultant Anaesthetist",
        ],
        ["Case no.", otCase.case_number || DASH],
      ],
    },
  );

  next = cardPair(
    doc,
    next + CARD_GAP,
    {
      title: "Proposed Anesthetic Technique & Risk Stratification",
      rows: [
        ["Proposed Plan", pac.anesthesia_plan || "General Anesthesia (GA)"],
        [
          "ASA Status",
          `ASA ${pac.asa_class || "I"}${pac.asa_emergency ? " E (Emergency)" : ""}`,
        ],
        [
          "Consent Risk",
          (pac.consent_risk_level || "NORMAL").replace(/_/g, " "),
        ],
        ["Blood Reserve", bloodReq],
      ],
    },
    {
      title: "Clinical Risk & Airway Evaluation",
      rows: [
        [
          "Airway Grade",
          `Mallampati ${pac.mallampati_score || airway.mallampati || "Class I/II"}`,
        ],
        ["Dentition", dentition],
        [
          "ICU / HDU Plan",
          pac.icu_booking_required
            ? "ICU Bed Reserved (Post-Op Step-up)"
            : "Standard PACU Recovery",
        ],
        ["Allergies", otCase.known_allergies || "No Known Drug Allergies (NKDA)"],
      ],
    },
  );

  next = textCard(
    doc,
    SIDE_M,
    next + CARD_GAP,
    U,
    "Statutory Pre-Anesthesia Informed Consent (NABH COP.13 / CoC 13)",
    "1. ANESTHETIC TECHNIQUE & CLINICAL ALTERNATIVES:\n" +
      `The nature, proposed technique (${pac.anesthesia_plan || "General Anesthesia (GA)"}), course, and expected clinical benefits of anesthesia have been explained to me in a language I clearly understand. Alternative techniques, their relative benefits, and the option of refusal have been discussed.\n\n` +
      "2. RECOGNIZED RISKS & POTENTIAL ADVERSE COMPLICATIONS:\n" +
      "I understand that all anesthesia involves inherent risks. Common minor risks: sore throat, hoarseness, shivering, nausea/vomiting, headache, dental/lip injury, venipuncture bruising, and transient hypotension. Rare serious complications: allergic anaphylaxis, respiratory depression/hypoxemia, aspiration, nerve injury, malignant hyperthermia, awareness under GA, or cardiovascular events.\n\n" +
      "3. CONVERSION OF TECHNIQUE & EMERGENCY RESUSCITATION:\n" +
      "I consent to any necessary conversion in anesthetic technique (e.g. converting regional/spinal or sedation to general endotracheal anesthesia) and to emergency life-saving interventions if deemed clinically necessary by the attending anesthesiology team.\n\n" +
      "4. BLOOD & BLOOD COMPONENT TRANSFUSION CONSENT:\n" +
      "I hereby give explicit consent for the administration of blood and blood components (PRBC, FFP, Platelets, Cryoprecipitate, or Whole Blood) should clinical condition or perioperative bleeding necessitate transfusion.\n\n" +
      "5. PRE-OPERATIVE FASTING (NPO) & PRE-EXISTING CONDITION DISCLOSURE:\n" +
      "I confirm adherence to pre-operative fasting (NPO) instructions (solids 6h, clear fluids 2h). I have truthfully disclosed all loose teeth, dentures, crowns, dental implants, known drug allergies, active medications, and family history of anesthesia complications.\n\n" +
      "6. POST-OPERATIVE RECOVERY & MULTIMODAL PAIN MANAGEMENT:\n" +
      "I understand I will be monitored in the Post-Anesthesia Care Unit (PACU) / ICU / HDU as clinically indicated, and a multimodal pain management protocol will be instituted.",
  );

  next += CARD_GAP;
  doc.setDrawColor(...C.navy);
  doc.setLineWidth(0.6);
  doc.rect(SIDE_M, next, U, 13, "S");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...C.navy);
  doc.text(
    "SURGICAL SITE / SIDE VERIFICATION (write in full — left, right, bilateral, midline, not applicable):",
    SIDE_M + CARD_PAD_X,
    next + 5,
  );
  doc.setDrawColor(...C.border);
  doc.setLineWidth(0.2);
  doc.line(SIDE_M + CARD_PAD_X, next + 10, SIDE_M + U - CARD_PAD_X, next + 10);
  next += 13;

  drawSignatureRow(
    doc,
    next + 6,
    "Patient / Authorized Representative (Signature / Thumbprint)",
    "Relationship & Contact / Date & Time",
  );
  drawSignatureRow(
    doc,
    next + 22,
    "Consultant Anaesthesiologist (Name, Reg. No. & Signature)",
    "Witness / Interpreter (Name, Language & Signature)",
  );

  stampFooters(doc, identity, true);
  open(doc);
}

/**
 * Pre-Anesthesia Assessment Form (Comprehensive Printable Sheet).
 *
 * Formatted with official hospital MIS letterhead (logo, facility address,
 * contact details, registration line) and standard NABH CoC 13 clinical blocks:
 * - Demographics, Anthropometrics & Baseline Vitals
 * - Medical, Previous Surgical & Family History, Habits & Allergies
 * - Airway Metrics, Dentition & Systemic Reviews
 * - Live CPOE Diagnostic Orders & Laboratory/Radiology Findings
 * - Proposed Anesthesia Technique, Cross-Specialty Consultations & Blood Logistics
 * - Clearance Outcome, Premedication Orders & Dual Signature Block
 */
export function printPreAnesthesiaAssessmentForm(otCase = {}, pac = {}) {
  const procedureName =
    (otCase.procedures || []).find((p) => p.is_primary)?.procedure_name ||
    otCase.procedure_name ||
    "Surgical Procedure";

  const { doc, identity, y } = startDoc({
    title: "Pre-Anesthesia Assessment Form (PAC)",
    subtitle: "NABH Pre-Operative Clinical Evaluation & Anesthesia Fitness Clearance",
    chipLabel: (pac.outcome || "PAC RECORD").replace(/_/g, " "),
    chipColor:
      pac.outcome === "FIT"
        ? [21, 128, 61]
        : pac.outcome === "UNFIT"
        ? [185, 28, 28]
        : [180, 83, 9],
  });

  const pacData = pac.assessment_data || {};
  const airway = pacData.airway || {};
  const systemic = pacData.systemic_reviews || {};
  const vitals = pac.vitals || pacData.vitals || {};
  const history = pacData.history || {};

  let next = cardPair(
    doc,
    y,
    {
      title: "Patient Particulars & Demographics",
      rows: [
        ["Name", otCase.patient_name || DASH],
        ["MRN / UHID", otCase.health_record_id || otCase.patient_id || DASH],
        [
          "Age / Sex",
          [otCase.age, otCase.gender].filter(Boolean).join(" / ") || DASH,
        ],
        ["Ward / Bed", `${otCase.ward_name || DASH} / ${otCase.bed_number || DASH}`],
        [
          "Blood Group",
          otCase.blood_group || pacData.demographics?.blood_group || DASH,
        ],
        [
          "Weight / Height",
          `${otCase.weight || pacData.demographics?.weight_kg || DASH} kg / ${
            otCase.height || pacData.demographics?.height_cm || DASH
          } cm`,
        ],
      ],
    },
    {
      title: "Surgical Case & Care Team",
      rows: [
        ["Procedure", procedureName],
        ["Surgeon", otCase.primary_surgeon_name || DASH],
        [
          "Anaesthetist",
          pac.anaesthetist_name || otCase.anaesthetist_name || "Consultant Anaesthesiologist",
        ],
        ["Department", otCase.department_name || otCase.department || DASH],
        ["Case No.", otCase.case_number || DASH],
      ],
    },
  );

  next = cardPair(
    doc,
    next + CARD_GAP,
    {
      title: "Baseline Vitals & Physical Examination",
      rows: [
        ["Blood Pressure", vitals.bp || "—"],
        ["Pulse / RR", `${vitals.pulse ? `${vitals.pulse} bpm` : "—"} / ${vitals.rr ? `${vitals.rr} /min` : "—"}`],
        ["SpO2 / Temp", `${vitals.spo2 ? `${vitals.spo2}% (RA)` : "—"} / ${vitals.temp ? `${vitals.temp} °F` : "—"}`],
        ["Fasting / NPO", vitals.fasting_hours ? `${vitals.fasting_hours} hrs` : "Standard NPO protocol"],
        ["Inspection Notes", vitals.physical_notes || "No overt pallor / jaundice / cyanosis"],
      ],
    },
    {
      title: "Airway & Dental Assessment",
      rows: [
        [
          "Mallampati Grade",
          `Class ${pac.mallampati_score || airway.mallampati || "I"}`,
        ],
        ["Mouth Opening", `${airway.mouth_opening || "ADEQUATE"} (${airway.mouth_opening_fingers || 3} fingers)`],
        ["Thyromental Dist.", `${airway.thyromental_distance || "6.5"} cm`],
        ["Dentition Notes", airway.dentures ? "Dentures Present" : airway.loose_teeth ? "Loose Teeth Identified" : "Intact Natural Dentition"],
        ["Difficult Airway", airway.difficult_airway_expected ? "YES (ANTICIPATED)" : "NO"],
      ],
    },
  );

  next = cardPair(
    doc,
    next + CARD_GAP,
    {
      title: "Medical History, Habits & Allergies",
      rows: [
        ["Medical Hx", history.summary_history || "No significant medical history documented"],
        ["Known Allergies", otCase.known_allergies || history.allergies || "No Known Drug Allergies (NKDA)"],
        ["Current Meds", history.current_medications || "None / Regular medications"],
        ["Smoking / Alcohol", `${history.smoking ? "Smoker" : "Non-smoker"} / ${history.alcohol || "Non-drinker"}`],
        ["Family Anesth Hx", history.family_anesthesia_complications ? "Adverse History Reported" : "NAD / Negative"],
      ],
    },
    {
      title: "Systemic Review Summary",
      rows: [
        ["Cardiovascular", systemic.cvs?.nad ? "NAD (Normal)" : [
          systemic.cvs?.hypertension && `HTN (${systemic.cvs?.htn_status || "Present"})`,
          systemic.cvs?.heart_failure && `HF (NYHA ${systemic.cvs?.hf_nyha || "Unspecified"})`,
          systemic.cvs?.arrhythmias && `Arrhythmia (${systemic.cvs?.arrhythmias})`,
          systemic.cvs?.valvular_disease && `Valvular (${systemic.cvs?.valvular_disease})`,
          systemic.cvs?.other_cvs,
        ].filter(Boolean).join("; ") || "CVS Disease Noted"],
        ["Respiratory", systemic.respiratory?.nad ? "NAD (Normal)" : [
          systemic.respiratory?.asthma && "Asthma",
          systemic.respiratory?.copd && "COPD",
          systemic.respiratory?.persistent_cough && "Cough",
          systemic.respiratory?.dyspnea && "Dyspnea",
          systemic.respiratory?.recent_uri && "URI",
          systemic.respiratory?.other_resp,
        ].filter(Boolean).join("; ") || "Respiratory Disease Noted"],
        ["Central Nervous", systemic.neuro?.mental_status || "Alert & Oriented"],
        ["GI & Renal", `${systemic.gi?.nad ? "GI Normal" : (systemic.gi?.other_gi || "GI Noted")} / ${systemic.renal?.nad ? "Renal Normal" : (systemic.renal?.other_renal || `Renal Noted${systemic.renal?.ccr_gfr ? ` (CCr ${systemic.renal?.ccr_gfr})` : ""}`)}`],
        ["Endocrine", systemic.endocrine?.nad ? "NAD (Normal)" : [
          systemic.endocrine?.diabetes && `DM (${systemic.endocrine?.diabetes_type || "Type 2"})`,
          systemic.endocrine?.lmp && `LMP: ${systemic.endocrine?.lmp}`,
          systemic.endocrine?.other_endocrine,
        ].filter(Boolean).join("; ") || "Endocrine Finding Noted"],
      ],
    },
  );

  next = cardPair(
    doc,
    next + CARD_GAP,
    {
      title: "Anesthesia Technique & Logistics",
      rows: [
        ["Proposed Technique", pac.anesthesia_plan || "General Anesthesia (GA)"],
        ["ASA Physical Status", `ASA ${pac.asa_class || "I"}${pac.asa_emergency ? " E (Emergency)" : ""}`],
        ["ICU / HDU Booking", pac.icu_booking_required ? "ICU Bed Reserved (Step-up)" : "Standard PACU"],
        ["Blood Arrangement", pac.blood_units_required ? (typeof pac.blood_units_required === "object" ? `${pac.blood_units_required.prbc || 0} PRBC, ${pac.blood_units_required.ffp || 0} FFP${pacData.plan_and_logistics?.blood_arrangements?.remarks ? ` (${pacData.plan_and_logistics.blood_arrangements.remarks})` : ""}` : `${pac.blood_units_required} U`) : "Type & Screen"],
        ["Consultation", pac.cross_specialty_consultation?.required ? `${pac.cross_specialty_consultation?.specialty || "Specialty"} (${pac.cross_specialty_consultation?.status || "Pending"})` : "No Referral Required"],
      ],
    },
    {
      title: "Clearance Decision & Directives",
      rows: [
        ["Fitness Outcome", pac.outcome || "FIT"],
        ["Next Review Due", pac.next_review_date ? formatIstDate(pac.next_review_date) : "N/A (Cleared)"],
        ["Premedication", pac.suggested_medications || "Per standard surgical protocol"],
        ["Fasting Directives", pac.optimization_instructions || "NPO 6h solids, 2h clear fluids"],
      ],
    },
  );

  next = textCard(
    doc,
    SIDE_M,
    next + CARD_GAP,
    U,
    "Anesthesiologist's Clinical Notes & Reassessment Directives",
    pac.notes || pac.reassessment_notes || "Patient evaluated and assessed clinically fit for planned surgical procedure under proposed anesthesia technique. Airway and baseline parameters confirmed.",
  );

  next += CARD_GAP + 4;
  drawSignatureRow(
    doc,
    next,
    "Patient / Authorized Relative (Informed & Agreed)",
    "Attending Consultant Anaesthesiologist (Signature & Stamp)",
  );

  stampFooters(doc, identity, true);
  open(doc);
}


